/**
 * Website data — reads inbound leads + inquiries from the marketing site,
 * which write to the SAME mfx-2026 Firestore (collections `leads`, `inquiries`).
 * READS ONLY. Falls back to a small sample when not signed in.
 */
import { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';

export interface WebLead {
  id: string; source: 'leads' | 'inquiries'; name: string; company?: string;
  email?: string; phone?: string; product?: string; industry?: string;
  message?: string; status: string; time: string;
}

const s = (...v: any[]): string => { for (const x of v) { if (x != null && String(x).trim()) return String(x).trim(); } return ''; };
const when = (d: any): string => {
  const t = d.createdAt?.toDate?.() || (d.createdAt ? new Date(d.createdAt) : null);
  if (!t || isNaN(+t)) return 'recent';
  const diff = (Date.now() - +t) / 86400000;
  if (diff < 1) return 'today'; if (diff < 2) return 'yesterday'; if (diff < 8) return `${Math.floor(diff)}d ago`;
  return t.toLocaleDateString();
};

function map(id: string, d: any, source: 'leads' | 'inquiries'): WebLead {
  return {
    id, source,
    name: s(d.name, d.fullName, d.contact, 'Website visitor'),
    company: s(d.company, d.companyName, d.organization),
    email: s(d.email), phone: s(d.phone),
    product: s(d.product, d.productInterest, d.subject),
    industry: s(d.industry), message: s(d.message, d.notes, d.details),
    status: s(d.status, 'new').toLowerCase(), time: when(d),
  };
}

async function read(name: 'leads' | 'inquiries', n: number): Promise<WebLead[]> {
  try {
    const snap = await getDocs(query(collection(db, name), orderBy('createdAt', 'desc'), limit(n)));
    return snap.docs.map((x) => map(x.id, x.data(), name));
  } catch {
    try { const snap = await getDocs(query(collection(db, name), limit(n))); return snap.docs.map((x) => map(x.id, x.data(), name)); }
    catch { return []; }
  }
}

const SAMPLE: WebLead[] = [
  { id: 's1', source: 'inquiries', name: 'Dana Cole', company: 'Verde Snacks', email: 'dana@verde.example', product: 'Stand-up pouch', industry: 'Food', message: 'Need 25k pouches, recyclable film.', status: 'new', time: 'today' },
  { id: 's2', source: 'leads', name: 'Marcus Lee', company: 'PureVita Supplements', email: 'marcus@purevita.example', product: 'Stick pack', industry: 'Nutraceutical', status: 'new', time: 'today' },
  { id: 's3', source: 'inquiries', name: 'Priya N.', company: 'Lumière Beauty', email: 'priya@lumiere.example', product: 'Shrink sleeve', industry: 'Beauty', message: 'Sample request for new SKU line.', status: 'contacted', time: 'yesterday' },
];

export interface WebLeadLoad { leads: WebLead[]; live: boolean; }

export function useWebLeads(demo: boolean): { data: WebLeadLoad; loading: boolean } {
  const [data, setData] = useState<WebLeadLoad>({ leads: demo ? SAMPLE : [], live: false });
  const [loading, setLoading] = useState(!demo);
  useEffect(() => {
    if (demo) { setData({ leads: SAMPLE, live: false }); setLoading(false); return; }
    let alive = true; setLoading(true);
    Promise.all([read('leads', 150), read('inquiries', 150)]).then(([a, b]) => {
      if (!alive) return;
      const all = [...a, ...b];
      setData(all.length ? { leads: all, live: true } : { leads: SAMPLE, live: false });
    }).catch(() => { if (alive) setData({ leads: SAMPLE, live: false }); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [demo]);
  return { data, loading };
}
